import React, { useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, Marker, Source } from 'react-map-gl/mapbox';
import type { LayerProps, MapGeoJSONFeature, MapMouseEvent, MapRef } from 'react-map-gl/mapbox';
import { FlyToInterpolator } from '@deck.gl/core';
import * as turf from '@turf/turf';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useUtmTaxonomyStore } from '@/store/utm-taxonomy-store';
import { useUtmMapSettings } from '@/hooks/utm';
import { bufferLineString } from '@/utils/utm-geometry';
import UtmMapControls from './UtmMapControls';
import UtmLocationSearch from './UtmLocationSearch';
import { buildUtmApiUrl } from '../shared/utm-backend-url';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INITIAL_VIEW_STATE = {
  longitude: -5.5471, // Ivory Coast center
  latitude: 7.54,
  zoom: 6.5,
  pitch: 0,
  bearing: 0,
};

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || '';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LngLatCoord = [number, number];

type InitialSelection = {
  geometry: GeoJSON.Geometry;
  selectionMode?: 'polygon' | 'radius' | 'corridor';
};

interface UtmMapProps {
  initialSelection?: InitialSelection | null;
  locationSearchLeftPx?: number;
  sidebarHidden?: boolean;
}

// ---------------------------------------------------------------------------
// Geometry helpers (local – mirrored from source)
// ---------------------------------------------------------------------------

const buildCircleGeometry = (center: LngLatCoord, edge: LngLatCoord): GeoJSON.Polygon | null => {
  const radiusMeters = turf.distance(turf.point(center), turf.point(edge), { units: 'meters' });
  if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) {
    return null;
  }

  const circle = turf.circle(center, radiusMeters / 1000, {
    units: 'kilometers',
    steps: 64,
  });

  return circle.geometry as GeoJSON.Polygon;
};

const lineStringFromPoints = (points: LngLatCoord[]): GeoJSON.LineString | null => {
  if (points.length < 2) {
    return null;
  }

  return {
    type: 'LineString',
    coordinates: points,
  };
};

const polygonFromPoints = (points: LngLatCoord[]): GeoJSON.Polygon | null => {
  if (points.length < 3) {
    return null;
  }

  return {
    type: 'Polygon',
    coordinates: [[...points, points[0]]],
  };
};

const toFeatureCollection = (features: GeoJSON.Feature[]): GeoJSON.FeatureCollection => {
  if (!features.length) {
    return { type: 'FeatureCollection', features: [] };
  }

  return {
    type: 'FeatureCollection',
    features,
  };
};

// ---------------------------------------------------------------------------
// Layer config factory
// ---------------------------------------------------------------------------

const createGroupLayerConfigs = (
  sourceId: string,
  groupCode: string,
): { fillLayer: LayerProps; polygonOutlineLayer: LayerProps; lineLayer: LayerProps; pointLayer: LayerProps } => {
  return {
    fillLayer: {
      id: `group-${groupCode}-fill`,
      type: 'fill',
      source: sourceId,
      'source-layer': 'default',
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        'fill-color': [
          'rgba',
          ['coalesce', ['get', 'fillR'], 255],
          ['coalesce', ['get', 'fillG'], 0],
          ['coalesce', ['get', 'fillB'], 0],
          ['/', ['coalesce', ['get', 'fillA'], 100], 255],
        ],
      },
    } as LayerProps,
    polygonOutlineLayer: {
      id: `group-${groupCode}-polygon-outline`,
      type: 'line',
      source: sourceId,
      'source-layer': 'default',
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        'line-color': [
          'rgba',
          ['coalesce', ['get', 'lineR'], 0],
          ['coalesce', ['get', 'lineG'], 0],
          ['coalesce', ['get', 'lineB'], 255],
          ['/', ['coalesce', ['get', 'lineA'], 200], 255],
        ],
        'line-width': ['coalesce', ['get', 'lineWidth'], 1],
      },
    } as LayerProps,
    lineLayer: {
      id: `group-${groupCode}-line`,
      type: 'line',
      source: sourceId,
      'source-layer': 'default',
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: {
        'line-color': [
          'rgba',
          ['coalesce', ['get', 'lineR'], 0],
          ['coalesce', ['get', 'lineG'], 0],
          ['coalesce', ['get', 'lineB'], 255],
          ['/', ['coalesce', ['get', 'lineA'], 200], 255],
        ],
        'line-width': ['coalesce', ['get', 'lineWidth'], 1],
      },
    } as LayerProps,
    pointLayer: {
      id: `group-${groupCode}-point`,
      type: 'circle',
      source: sourceId,
      'source-layer': 'default',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-color': [
          'rgba',
          ['coalesce', ['get', 'fillR'], 255],
          ['coalesce', ['get', 'fillG'], 0],
          ['coalesce', ['get', 'fillB'], 0],
          ['/', ['coalesce', ['get', 'fillA'], 100], 255],
        ],
        'circle-stroke-color': [
          'rgba',
          ['coalesce', ['get', 'lineR'], 0],
          ['coalesce', ['get', 'lineG'], 0],
          ['coalesce', ['get', 'lineB'], 255],
          ['/', ['coalesce', ['get', 'lineA'], 200], 255],
        ],
        'circle-stroke-width': 1,
        'circle-radius': ['coalesce', ['get', 'pointRadius'], 5],
      },
    } as LayerProps,
  };
};

const buildHiddenZoneFilter = (hiddenZoneIds: string[]) => {
  if (!hiddenZoneIds.length) {
    return true;
  }

  return ['!', ['in', ['to-string', ['get', 'zoneId']], ['literal', hiddenZoneIds]]] as any;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const UtmMap: React.FC<UtmMapProps> = ({
  initialSelection,
  locationSearchLeftPx = 16,
  sidebarHidden = false,
}) => {
  const {
    groups,
    activeGroups,
    fetchTaxonomy,
    basemapStyle,
    selectionMode,
    selectionResults,
    clearSelection,
    selectZone,
    corridorWidth,
    setSelectionMode,
    selectionFeatures,
    activeSelectionFeatureId,
    addSelectionFeature,
    setActiveSelectionFeature,
    intersectionZoneIdsByFeatureId,
    visibleIntersectionZoneIdsByFeatureId,
    hoveredZoneIds,
    zoneGeometryById,
  } = useUtmTaxonomyStore();
  const { data: mapSettings } = useUtmMapSettings();

  const mapRef = useRef<MapRef>(null);
  const [cursorCoords, setCursorCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE as any);

  const [draftPoints, setDraftPoints] = useState<LngLatCoord[]>([]);
  const [hoverCoord, setHoverCoord] = useState<LngLatCoord | null>(null);
  const hasHydratedInitialSelection = useRef(false);
  const hasAppliedDefaultMapSettings = useRef(false);

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  useEffect(() => {
    fetchTaxonomy();
  }, [fetchTaxonomy]);

  // Add scale control once the map is ready
  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const map = mapRef.current.getMap();
    let scaleControl: any;

    import('mapbox-gl').then((mapboxgl) => {
      scaleControl = new mapboxgl.ScaleControl({
        maxWidth: 100,
        unit: 'metric',
      });
      map.addControl(scaleControl, 'bottom-left');
    });

    return () => {
      if (scaleControl) {
        map.removeControl(scaleControl);
      }
    };
  }, []);

  // Reset draft state whenever the selection mode changes
  useEffect(() => {
    setDraftPoints([]);
    setHoverCoord(null);
  }, [selectionMode]);

  useEffect(() => {
    if (hasAppliedDefaultMapSettings.current || !mapSettings) {
      return;
    }

    setViewState((prev: any) => ({
      ...prev,
      longitude: mapSettings.longitude,
      latitude: mapSettings.latitude,
      zoom: mapSettings.zoom,
    }));
    hasAppliedDefaultMapSettings.current = true;
  }, [mapSettings]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    // Ensure canvas matches container after layout width change (sidebar toggle).
    const resizeHandle = window.requestAnimationFrame(() => {
      map.resize();
    });

    return () => {
      window.cancelAnimationFrame(resizeHandle);
    };
  }, [sidebarHidden]);

  useEffect(() => {
    if (hasHydratedInitialSelection.current || !initialSelection?.geometry) {
      return;
    }

    hasHydratedInitialSelection.current = true;

    clearSelection();
    setSelectionMode('none');
    void addSelectionFeature(initialSelection.geometry);

    const bounds = turf.bbox(initialSelection.geometry);
    if (
      mapRef.current &&
      bounds.length === 4 &&
      Number.isFinite(bounds[0]) &&
      Number.isFinite(bounds[1]) &&
      Number.isFinite(bounds[2]) &&
      Number.isFinite(bounds[3])
    ) {
      mapRef.current.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ],
        { padding: 48, duration: 700 },
      );
    }
  }, [initialSelection, addSelectionFeature, clearSelection, setSelectionMode]);

  // Keyboard shortcut: Escape cancels current drawing
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearDrawingState();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------

  const hiddenIntersectionZoneIds = useMemo(() => {
    if (!activeSelectionFeatureId) return [] as string[];
    const all = intersectionZoneIdsByFeatureId[activeSelectionFeatureId] ?? [];
    const visible = new Set(
      visibleIntersectionZoneIdsByFeatureId[activeSelectionFeatureId] ?? all,
    );

    return all.filter((zoneId) => !visible.has(zoneId));
  }, [
    activeSelectionFeatureId,
    intersectionZoneIdsByFeatureId,
    visibleIntersectionZoneIdsByFeatureId,
  ]);

  const activeGroupConfigs = useMemo(() => {
    const hiddenZoneFilter = buildHiddenZoneFilter(hiddenIntersectionZoneIds);

    return Array.from(activeGroups)
      .map((groupCode) => {
        const group = groups.find((g) => g.code === groupCode);
        if (!group) {
          return null;
        }

        const sourceId = `group-source-${group.id}`;
        const tileUrl = buildUtmApiUrl(`/utm/tiles/groups/${group.id}/{z}/{x}/{y}.mvt`);
        const layers = createGroupLayerConfigs(sourceId, groupCode);

        return {
          group,
          sourceId,
          tileUrl,
          ...layers,
          fillLayer: {
            ...layers.fillLayer,
            filter: ['all', layers.fillLayer.filter as any, hiddenZoneFilter],
          } as LayerProps,
          polygonOutlineLayer: {
            ...layers.polygonOutlineLayer,
            filter: ['all', layers.polygonOutlineLayer.filter as any, hiddenZoneFilter],
          } as LayerProps,
          lineLayer: {
            ...layers.lineLayer,
            filter: ['all', layers.lineLayer.filter as any, hiddenZoneFilter],
          } as LayerProps,
          pointLayer: {
            ...layers.pointLayer,
            filter: ['all', layers.pointLayer.filter as any, hiddenZoneFilter],
          } as LayerProps,
        };
      })
      .filter(Boolean) as Array<{
      group: { id: string; code: string };
      sourceId: string;
      tileUrl: string;
      fillLayer: LayerProps;
      polygonOutlineLayer: LayerProps;
      lineLayer: LayerProps;
      pointLayer: LayerProps;
    }>;
  }, [groups, activeGroups, hiddenIntersectionZoneIds]);

  const draftGeometry = useMemo((): GeoJSON.Geometry | null => {
    if (selectionMode === 'none') {
      return null;
    }

    if (selectionMode === 'radius') {
      if (draftPoints.length !== 1 || !hoverCoord) {
        return null;
      }
      return buildCircleGeometry(draftPoints[0], hoverCoord);
    }

    if (selectionMode === 'polygon') {
      const points = hoverCoord ? [...draftPoints, hoverCoord] : draftPoints;
      if (points.length >= 3) {
        return polygonFromPoints(points);
      }
      if (points.length >= 2) {
        return lineStringFromPoints(points);
      }
      return null;
    }

    if (selectionMode === 'corridor') {
      const points = hoverCoord ? [...draftPoints, hoverCoord] : draftPoints;
      if (points.length < 2) {
        return null;
      }

      const line = lineStringFromPoints(points);
      if (!line) {
        return null;
      }

      try {
        return bufferLineString(line, corridorWidth);
      } catch (error) {
        console.error('Failed to buffer draft corridor line:', error);
        return null;
      }
    }

    return null;
  }, [selectionMode, draftPoints, hoverCoord, corridorWidth]);

  const visibleZoneIds = useMemo(() => {
    if (!activeSelectionFeatureId) return null;
    const visible = visibleIntersectionZoneIdsByFeatureId[activeSelectionFeatureId];
    if (!visible) return null;
    return new Set(visible);
  }, [activeSelectionFeatureId, visibleIntersectionZoneIdsByFeatureId]);

  const highlightGeometryCollection = useMemo<GeoJSON.FeatureCollection>(() => {
    if (!selectionResults?.zones?.length && hoveredZoneIds.length === 0) {
      return { type: 'FeatureCollection', features: [] };
    }

    const zoneById = new globalThis.Map<string, any>();
    (selectionResults?.zones ?? []).forEach((zone) => {
      zoneById.set(zone.id, zone);
    });

    const shouldRenderZone = (zoneId: string) =>
      (visibleZoneIds ? visibleZoneIds.has(zoneId) : true) || hoveredZoneIds.includes(zoneId);

    const zoneIdsFromResults = (selectionResults?.zones ?? [])
      .map((zone) => zone.id)
      .filter((zoneId) => shouldRenderZone(zoneId));
    const hoveredOutsideResults = hoveredZoneIds.filter((zoneId) => !zoneById.has(zoneId));
    const allZoneIds = Array.from(new Set([...zoneIdsFromResults, ...hoveredOutsideResults]));

    return {
      type: 'FeatureCollection',
      features: allZoneIds
        .map((zoneId, index: number) => {
          const zone = zoneById.get(zoneId);
          const fallbackGeometry = zoneGeometryById[zoneId];
          const geometry = zone?.geometry?.geom || zone?.geometry || fallbackGeometry;
          const zoneName = zone?.zoneName || zoneId;
          if (!geometry) {
            return null;
          }

          return {
            type: 'Feature',
            properties: {
              zoneId,
              zoneName,
            },
            id: zoneId || `highlight-${index}`,
            geometry,
          } as GeoJSON.Feature;
        })
        .filter(Boolean) as GeoJSON.Feature[],
    };
  }, [selectionResults, hoveredZoneIds, visibleZoneIds, zoneGeometryById]);

  const interactiveLayerIds = useMemo(() => {
    const groupLayerIds = activeGroupConfigs.flatMap((config) => [
      config.fillLayer.id as string,
      config.polygonOutlineLayer.id as string,
      config.lineLayer.id as string,
      config.pointLayer.id as string,
    ]);

    return [
      ...groupLayerIds,
      'selection-final-fill',
      'selection-final-line',
      'selection-final-line-active',
      'highlight-fill',
      'highlight-line',
    ];
  }, [activeGroupConfigs]);

  const selectionFinalFeatures = useMemo<GeoJSON.Feature[]>(() => {
    return selectionFeatures.map((feature, index) => ({
      type: 'Feature',
      properties: {
        selectionId: feature.id,
        selectionIndex: index + 1,
        isActive: feature.id === activeSelectionFeatureId,
      },
      geometry: feature.geometry,
    }));
  }, [selectionFeatures, activeSelectionFeatureId]);
  const selectionFinalData = useMemo(
    () => toFeatureCollection(selectionFinalFeatures),
    [selectionFinalFeatures],
  );
  const selectionDraftData = useMemo(() => {
    if (!draftGeometry) {
      return toFeatureCollection([]);
    }
    return toFeatureCollection([
      {
        type: 'Feature',
        properties: {},
        geometry: draftGeometry,
      },
    ]);
  }, [draftGeometry]);
  const selectionCentroids = useMemo(() => {
    if (selectionFinalFeatures.length <= 1) {
      return [];
    }

    return selectionFinalFeatures
      .map((feature, index) => {
        try {
          const center = turf.centerOfMass(feature as any);
          const coords = center?.geometry?.coordinates;
          if (!Array.isArray(coords) || coords.length !== 2) {
            return null;
          }
          return {
            id: String(feature.properties?.selectionId || `selection-${index + 1}`),
            number: index + 1,
            lng: coords[0],
            lat: coords[1],
            active: String(feature.properties?.selectionId || '') === activeSelectionFeatureId,
          };
        } catch (error) {
          console.error('Failed to compute centroid for selection feature:', error);
          return null;
        }
      })
      .filter(Boolean) as Array<{ id: string; number: number; lng: number; lat: number; active: boolean }>;
  }, [selectionFinalFeatures, activeSelectionFeatureId]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const clearDrawingState = () => {
    setDraftPoints([]);
    setHoverCoord(null);
  };

  const finalizeSelectionGeometry = async (geometry: GeoJSON.Geometry | null) => {
    if (!geometry) {
      return;
    }

    await addSelectionFeature(geometry);

    setDraftPoints([]);
    setHoverCoord(null);
  };

  const handleLocationClick = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setViewState({
          longitude: position.coords.longitude,
          latitude: position.coords.latitude,
          zoom: 15,
          pitch: 0,
          bearing: 0,
          transitionDuration: 2000,
          transitionInterpolator: new FlyToInterpolator(),
        });
      },
      (error) => {
        alert(`Unable to access your location: ${error.message}`);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  const handleLocationSelect = (lat: number, lon: number, _name: string) => {
    setViewState({
      longitude: lon,
      latitude: lat,
      zoom: 13,
      pitch: 0,
      bearing: 0,
      transitionDuration: 2000,
      transitionInterpolator: new FlyToInterpolator(),
    });
  };

  const handleMapClick = (event: MapMouseEvent) => {
    const features = (event.features || []) as MapGeoJSONFeature[];
    const clickedSelection = features.find((feature: MapGeoJSONFeature) => {
      const selectionId = feature?.properties?.selectionId;
      return typeof selectionId === 'string';
    });
    const clickedFeature = features.find((feature: MapGeoJSONFeature) => {
      const zoneId = feature?.properties?.zoneId;
      return typeof zoneId === 'string' || typeof zoneId === 'number';
    });

    if (selectionMode === 'none') {
      if (clickedSelection?.properties?.selectionId) {
        setActiveSelectionFeature(String(clickedSelection.properties.selectionId));
        return;
      }

      if (clickedFeature?.properties?.zoneId) {
        selectZone(String(clickedFeature.properties.zoneId));
        return;
      }

      setActiveSelectionFeature(null);
      return;
    }

    const lngLat: LngLatCoord = [event.lngLat.lng, event.lngLat.lat];

    if (selectionMode === 'radius') {
      if (draftPoints.length === 0) {
        setDraftPoints([lngLat]);
      } else {
        void finalizeSelectionGeometry(buildCircleGeometry(draftPoints[0], lngLat));
      }
      return;
    }

    setDraftPoints((prev) => [...prev, lngLat]);
  };

  const handleMapDoubleClick = (event: MapMouseEvent) => {
    if (selectionMode === 'none') {
      return;
    }

    event.preventDefault();

    if (selectionMode === 'polygon') {
      void finalizeSelectionGeometry(polygonFromPoints(draftPoints));
      return;
    }

    if (selectionMode === 'corridor') {
      const line = lineStringFromPoints(draftPoints);
      if (!line) {
        return;
      }

      try {
        void finalizeSelectionGeometry(bufferLineString(line, corridorWidth));
      } catch (error) {
        console.error('Failed to buffer corridor line:', error);
      }
    }
  };

  const handleMapMouseMove = (event: MapMouseEvent) => {
    setCursorCoords({
      lat: event.lngLat.lat,
      lon: event.lngLat.lng,
    });

    if (selectionMode !== 'none') {
      setHoverCoord([event.lngLat.lng, event.lngLat.lat]);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="relative w-full h-full">
      <UtmMapControls onLocationClick={handleLocationClick} />
      <UtmLocationSearch
        onLocationSelect={handleLocationSelect}
        leftOffsetPx={locationSearchLeftPx}
      />

      {/* Cursor coordinates overlay */}
      {cursorCoords && (
        <div className="pointer-events-none absolute bottom-8 right-2 z-10 rounded bg-primary/80 px-2 py-1 text-[11px] font-mono text-primary-foreground">
          {cursorCoords.lat.toFixed(6)}, {cursorCoords.lon.toFixed(6)}
        </div>
      )}

      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle={basemapStyle}
        style={{ width: '100%', height: '100%' }}
        projection={{ name: 'globe' }}
        fog={{
          range: [0.8, 8],
          color: '#d8f0ff',
          'horizon-blend': 0.1,
          'high-color': '#245bde',
          'space-color': '#000000',
          'star-intensity': 0.15,
        }}
        longitude={viewState.longitude}
        latitude={viewState.latitude}
        zoom={viewState.zoom}
        pitch={viewState.pitch}
        bearing={viewState.bearing}
        onMove={(event) => setViewState(event.viewState)}
        onClick={handleMapClick}
        onDblClick={handleMapDoubleClick}
        onMouseMove={handleMapMouseMove}
        doubleClickZoom={selectionMode === 'none'}
        interactiveLayerIds={interactiveLayerIds}
        cursor={selectionMode === 'none' ? 'grab' : 'crosshair'}
      >
        {/* MVT layers for each active group */}
        {activeGroupConfigs.map((config) => (
          <Source
            key={config.sourceId}
          id={config.sourceId}
          type="vector"
          tiles={[config.tileUrl]}
          >
            <Layer {...config.fillLayer} />
            <Layer {...config.polygonOutlineLayer} />
            <Layer {...config.lineLayer} />
            <Layer {...config.pointLayer} />
          </Source>
        ))}

        {/* Draft drawing geometry (while user is still placing points) */}
        <Source id="selection-draft" type="geojson" data={selectionDraftData as any}>
          <Layer
            id="selection-draft-fill"
            type="fill"
            paint={{ 'fill-color': 'rgba(255,0,0,0.1)' }}
          />
          <Layer
            id="selection-draft-line"
            type="line"
            paint={{
              'line-color': 'rgba(0,0,0,1)',
              'line-width': 2,
              'line-dasharray': [2, 2],
            }}
          />
        </Source>

        {/* Finalized selection geometry */}
        <Source id="selection-final" type="geojson" data={selectionFinalData as any}>
          <Layer
            id="selection-final-fill"
            type="fill"
            paint={{ 'fill-color': 'rgba(255,0,0,0.1)' }}
          />
          <Layer
            id="selection-final-line"
            type="line"
            paint={{ 'line-color': 'rgba(0,0,0,1)', 'line-width': 2 }}
          />
          <Layer
            id="selection-final-line-active"
            type="line"
            filter={['==', ['get', 'selectionId'], activeSelectionFeatureId || '__none__']}
            paint={{ 'line-color': 'rgba(0,0,0,1)', 'line-width': 4 }}
          />
        </Source>

        {selectionCentroids.map((centroid) => (
          <Marker key={centroid.id} longitude={centroid.lng} latitude={centroid.lat} anchor="center">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveSelectionFeature(centroid.id);
              }}
              className={`flex h-6 w-6 items-center justify-center rounded-full border-2 text-xs font-semibold shadow ${
                centroid.active
                  ? 'border-black bg-black text-white'
                  : 'border-black bg-[#123a6f] text-white'
              }`}
              aria-label={`Select drawing ${centroid.number}`}
            >
              {centroid.number}
            </button>
          </Marker>
        ))}

        {/* Highlight for intersecting zones returned by query */}
        <Source id="highlight-source" type="geojson" data={highlightGeometryCollection as any}>
          <Layer
            id="highlight-fill"
            type="fill"
            paint={{ 'fill-color': 'rgba(0,255,255,0.35)' }}
          />
          <Layer
            id="highlight-line"
            type="line"
            paint={{ 'line-color': 'rgba(0,255,255,1)', 'line-width': 3 }}
          />
        </Source>
      </Map>
    </div>
  );
};

export default UtmMap;
